import React, { useState, useEffect } from 'react';
import './MarketplaceV2.css';

const MarketplaceV2 = () => {
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerPicks, setSellerPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('sellers'); // 'sellers' or 'picks'

  useEffect(() => {
    loadSellers();
  }, []);

  const loadSellers = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://tj3ekurow57hlcmnyojtcn7nxi0gubuu.lambda-url.us-east-1.on.aws/api/cards');
      
      if (!response.ok) {
        throw new Error('API call failed');
      }
      
      const data = await response.json();
      
      // Transform API data to match frontend format
      const transformedSellers = data.map(card => ({
        id: card.card_key,
        name: card.name,
        bio: card.description,
        verified: true,
        tier: 'gold',
        // Backtest performance
        backtest_picks: card.expected_performance.total_picks,
        backtest_wins: card.expected_performance.wins,
        backtest_losses: card.expected_performance.losses,
        backtest_win_rate: card.expected_performance.win_rate,
        backtest_roi: card.expected_performance.roi,
        // Live performance
        total_picks: card.live_performance.total_picks,
        wins: card.live_performance.wins,
        losses: card.live_performance.losses,
        win_rate: card.live_performance.win_rate,
        roi: card.live_performance.roi,
        price_per_pick: 0
      }));
      
      setSellers(transformedSellers);
    } catch (error) {
      console.error('Error loading sellers:', error);
      setSellers([]);
    }
    setLoading(false);
  };

  const loadSellerPicks = async (cardKey) => {
    setLoading(true);
    try {
      const response = await fetch(`https://tj3ekurow57hlcmnyojtcn7nxi0gubuu.lambda-url.us-east-1.on.aws/api/cards/${cardKey}/picks`);
      
      if (!response.ok) {
        throw new Error('API call failed');
      }
      
      const data = await response.json();
      setSellerPicks(data || []);
    } catch (error) {
      console.error('Error loading picks:', error);
      // Fallback picks for demo
      const mockPicks = [
        {
          id: `${cardKey}_pick_1`,
          matchup: 'Lakers @ Suns',
          selection: 'Over 229.5',
          odds: -108,
          confidence: 'high',
          price: 0,
          game_time: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          reasoning: 'AI model detected value based on pace metrics and defensive efficiency ratings.'
        }
      ];
      setSellerPicks(mockPicks);
    }
    setLoading(false);
  };

  const selectSeller = (seller) => {
    setSelectedSeller(seller);
    setView('picks');
    loadSellerPicks(seller.id);
  };

  const purchasePick = async (pick) => {
    try {
      const token = localStorage.getItem('idToken');
      const headers = token 
        ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        : { 'Content-Type': 'application/json' };
      
      const response = await fetch('https://563t3yrmw5d42zyd7gw6eeeun40htlid.lambda-url.us-east-1.on.aws/api/account/bets', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          matchup: pick.matchup,
          selection: pick.selection,
          odds: pick.odds,
          game_time: pick.game_time,
          source: 'bet_slip',
          source_pick_id: pick.pick_id
        })
      });
      
      if (response.ok) {
        alert(`✅ ${pick.selection} added to bet slip!`);
      } else {
        throw new Error('Failed to add');
      }
    } catch (error) {
      console.error('Error adding to bet slip:', error);
      alert('❌ Failed to add pick to bet slip. Please try again.');
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: '#cd7f32',
      silver: '#c0c0c0', 
      gold: '#ffd700',
      platinum: '#e5e4e2'
    };
    return colors[tier] || '#666';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (view === 'picks' && selectedSeller) {
    return (
      <div className="marketplace-v2">
        <div className="marketplace-header">
          <button 
            className="back-btn"
            onClick={() => setView('sellers')}
          >
            ← Back to Sellers
          </button>
          <div className="seller-header">
            <h2>{selectedSeller.name}</h2>
            <div className="seller-stats-header">
              <div className="stats-group">
                <span className="stats-title">📊 Backtest:</span>
                <span className="stat">{selectedSeller.backtest_win_rate?.toFixed(1) || 0}% WR</span>
                <span className="stat">+{selectedSeller.backtest_roi || 0}% ROI</span>
                <span className="stat">{selectedSeller.backtest_picks || 0} picks</span>
              </div>
              <div className="stats-group live">
                <span className="stats-title">🔴 Live:</span>
                <span className="stat">{selectedSeller.wins || 0}W-{selectedSeller.losses || 0}L</span>
              </div>
            </div>
          </div>
        </div>

        <div className="picks-container">
          {loading ? (
            <div className="loading">Loading picks...</div>
          ) : sellerPicks.length === 0 ? (
            <div className="empty-state">
              <p>No picks available from this seller</p>
            </div>
          ) : (
            <div className="picks-grid">
              {sellerPicks.map(pick => (
                <div key={pick.id} className="pick-card">
                  <div className="pick-header">
                    <span className={`confidence ${pick.confidence}`}>
                      {pick.confidence.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="pick-details">
                    <div className="matchup">{pick.matchup}</div>
                    <div className="selection">{pick.selection}</div>
                    <div className="odds">Odds: {pick.odds > 0 ? `+${pick.odds}` : pick.odds}</div>
                    <div className="game-time">
                      Game: {pick.date && pick.game_time ? new Date(`${pick.date}T${pick.game_time}`).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'TBD'}
                    </div>
                    {pick.result && pick.result !== 'pending' && (
                      <div className={`pick-result ${pick.result}`}>
                        Result: {pick.result.toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="reasoning">
                    <strong>Analysis:</strong> {pick.reasoning}
                  </div>

                  <button 
                    className="purchase-btn"
                    onClick={() => purchasePick(pick)}
                  >
                    📋 Add to Bet Slip
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-v2">
      <div className="marketplace-header">
        <h2>🏆 Verified Pick Sellers</h2>
        <p>Professional handicappers with proven track records</p>
      </div>

      <div className="sellers-container">
        {loading ? (
          <div className="loading">Loading sellers...</div>
        ) : (
          <div className="sellers-grid">
            {sellers.map(seller => (
              <div key={seller.id} className="seller-card">
                <div className="seller-header">
                  <div className="seller-info">
                    <h3>{seller.name}</h3>
                    <div 
                      className="tier-badge"
                      style={{ backgroundColor: getTierColor(seller.tier) }}
                    >
                      {seller.tier.toUpperCase()}
                    </div>
                  </div>
                  {seller.verified && (
                    <div className="verified-badge">✅ Verified</div>
                  )}
                </div>

                <div className="seller-bio">
                  {seller.bio}
                </div>

                <div className="seller-stats">
                  <div className="stats-section">
                    <div className="stats-label">📊 Backtest</div>
                    <div className="stat-row">
                      <div className="stat">
                        <span className="stat-value">{seller.backtest_win_rate?.toFixed(1) || 0}%</span>
                        <span className="stat-label">Win Rate</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">+{seller.backtest_roi || 0}%</span>
                        <span className="stat-label">ROI</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{seller.backtest_picks || 0}</span>
                        <span className="stat-label">Picks</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="stats-section live">
                    <div className="stats-label">🔴 Live Record</div>
                    <div className="stat-row">
                      <div className="stat">
                        <span className="stat-value">{seller.win_rate?.toFixed(1) || 0}%</span>
                        <span className="stat-label">Win Rate</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{seller.wins || 0}W-{seller.losses || 0}L</span>
                        <span className="stat-label">Record</span>
                      </div>
                    </div>
                  </div>
                </div>

                <button 
                  className="view-picks-btn"
                  onClick={() => selectSeller(seller)}
                >
                  View Available Picks
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceV2;
